/**
 * VideoPlayer — LC 학습 Video 컴포넌트 오케스트레이터.
 * - QueturePlayer(externalControls) + PlayerBar + CompleteCTA + Pause UI (optional)
 * - 상단 [영상 + PausePointList] 고정, 하단 활성 시 swap (guide/solve/result) — 스펙 §5
 * - skipPolicy / speedPolicy / requiredWatchPercent 풀 연결
 * - 활성 시 maxScrubSec = activePoint.time / 1000 으로 PlayerBar cap
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { QueturePlayer } from '@quebon/shared-learning'
import type { QuetureApiInterface, QueturePlayerHandle } from '@quebon/shared-learning'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import {
  completeSession,
  sendVideoHeartbeat,
  sendVideoEvent,
  submitPauseQuizOfflineComplete,
  type LcVideoDataResponse,
  type MaterialNotice,
  type PauseAndGoPoint,
} from '@/api/lcLearning'
import { getApiErrorMessage } from '@/utils/apiError'
import { Card } from './ui/Card'
import PlayerBar from './video/PlayerBar'
import CompleteCTA from './video/CompleteCTA'
import { MaterialBookOpenDialog } from './video/MaterialBookOpenDialog'
import { usePlayerState } from './video/usePlayerState'
import { SplitContainer } from './layout/SplitContainer'
import PauseGuideCard from './pause/PauseGuideCard'
import PauseResultCard from './pause/PauseResultCard'
import PauseSolvePanel from './pause/PauseSolvePanel'
import { usePauseQuizState } from './pause/usePauseQuizState'
import type { RuntimeMode } from './ComponentRunner'

function extractQidFromUrl(url: string | null): string {
  if (!url) return ''
  const match = url.match(/\/quetures\/([^/]+)\//)
  return match?.[1] ?? ''
}

const PAUSE_CAP_TOLERANCE_SEC = 0.05

function pausePointKey(time: number, quizId: string | null): string {
  return `${time}:${quizId ?? ''}`
}

interface Props {
  componentProgressId: number | null
  enrollmentId: number
  componentType: string
  videoData: LcVideoDataResponse
  isFirstAttempt: boolean
  materialNotice?: MaterialNotice | null
  runtimeMode?: RuntimeMode
  /** 교사 지정 복습 레슨 여부 — Task 6 에서 book-skip 로직 구현 예정. */
  isDesignatedReview?: boolean
  onComplete: () => void
}

export default function VideoPlayer({
  componentProgressId,
  enrollmentId,
  componentType,
  videoData,
  isFirstAttempt,
  materialNotice = null,
  runtimeMode = 'live',
  isDesignatedReview,
  onComplete,
}: Props) {
  const isLive = runtimeMode === 'live'
  const { t } = useTranslation('learner')
  const playerRef = useRef<QueturePlayerHandle | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const state = usePlayerState(playerRef)
  const seekPlayer = state.seek

  const [completing, setCompleting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activePoint, setActivePoint] = useState<PauseAndGoPoint | null>(null)
  const [acknowledgedPausePoints, setAcknowledgedPausePoints] = useState<Set<string>>(
    () => new Set(),
  )
  const [guideSubmitting, setGuideSubmitting] = useState(false)
  const guideSubmittingRef = useRef(false)
  const [splitRatio, setSplitRatio] = useState(63)

  const pauseAndGoPoints = useMemo(
    () => videoData.pauseAndGoPoints ?? [],
    [videoData.pauseAndGoPoints],
  )
  const isPauseQuizDisabled = pauseAndGoPoints.length === 0
  const [materialBookDialogDismissed, setMaterialBookDialogDismissed] = useState(false)
  const materialBookDialogOpen =
    materialNotice != null &&
    !materialBookDialogDismissed &&
    isLive &&
    isFirstAttempt &&
    !isDesignatedReview &&
    pauseAndGoPoints.length > 0
  const previousPauseResponses = useMemo(
    () => videoData.previousPauseResponses ?? [],
    [videoData.previousPauseResponses],
  )
  const previousPauseKeys = useMemo(
    () => new Set(previousPauseResponses.map((r) => pausePointKey(r.pauseTimeMs, r.quizId))),
    [previousPauseResponses],
  )
  const orderedPausePoints = useMemo(
    () => [...pauseAndGoPoints].sort((a, b) => a.time - b.time),
    [pauseAndGoPoints],
  )

  const pauseState = usePauseQuizState({
    videoData,
    isFirstAttempt,
    activePoint,
    previousResponses: previousPauseResponses,
    isDesignatedReview,
  })

  const triggeredPointsRef = useRef<Set<string>>(new Set())
  const heartbeatQueueRef = useRef<Promise<void>>(Promise.resolve())
  const heartbeatRemainderSecRef = useRef(0)
  const playStartPositionSecRef = useRef(videoData.lastPositionSec)
  const hadActivePointRef = useRef(false)
  const manualSeekingRef = useRef<boolean>(false)
  const seekTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const videoContainerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    return () => {
      if (seekTimerRef.current) clearTimeout(seekTimerRef.current)
    }
  }, [])

  useEffect(() => {
    if (!activePoint) {
      if (hadActivePointRef.current) {
        const playBtn = containerRef.current?.querySelector<HTMLElement>(
          'button[data-action="toggle-play"]',
        )
        playBtn?.focus()
      }
      hadActivePointRef.current = false
      return
    }
    hadActivePointRef.current = true
    const activeCard = containerRef.current?.querySelector<HTMLElement>(
      '[data-region="pause-active-card"]',
    )
    const target = activeCard?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    )
    const raf = requestAnimationFrame(() => {
      activeCard?.scrollIntoView({ block: 'nearest' })
      target?.focus({ preventScroll: true })
    })
    return () => cancelAnimationFrame(raf)
  }, [activePoint])

  const quetureApi = useMemo<QuetureApiInterface>(
    () => ({
      getQuetureInfo: async () => {
        throw new Error('Direct hlsUrl is used; this should not be called')
      },
    }),
    [],
  )

  const hlsUrl = useMemo(() => {
    if (!videoData.hlsUrl) return null
    if (videoData.hlsUrl.startsWith('http')) return videoData.hlsUrl
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '/api'
    if (apiBaseUrl.startsWith('http')) {
      const url = new URL(apiBaseUrl)
      return `${url.origin}${videoData.hlsUrl}`
    }
    return `${window.location.origin}${videoData.hlsUrl}`
  }, [videoData.hlsUrl])

  const watchPercent = useMemo(() => {
    if (state.durationSec <= 0) return 0
    return Math.floor((state.maxProgressSec / state.durationSec) * 100)
  }, [state.maxProgressSec, state.durationSec])

  const allPausePointsAcknowledged = pauseAndGoPoints.every(
    (point) =>
      acknowledgedPausePoints.has(pausePointKey(point.time, point.quizId)) ||
      previousPauseKeys.has(pausePointKey(point.time, point.quizId)),
  )
  const canComplete =
    watchPercent >= videoData.requiredWatchPercent &&
    (!isLive || allPausePointsAcknowledged)

  const derivedMarkers = useMemo<ReadonlyArray<{ time: number; status: 'pending' | 'completed' }>>(
    () =>
      pauseAndGoPoints.map((pt) => {
        const isCompleted =
          acknowledgedPausePoints.has(pausePointKey(pt.time, pt.quizId)) || pauseState.statusFor(pt) === 'done'
        if (isCompleted) return { time: pt.time, status: 'completed' as const }
        return { time: pt.time, status: 'pending' as const }
      }),
    [acknowledgedPausePoints, pauseAndGoPoints, pauseState],
  )

  const queueHeartbeat = useCallback(
    (
      data: {
        startTimeSec: number
        endTimeSec: number
        playbackSpeed: number
        wasSeeking: boolean
      },
    ): Promise<void> => {
      if (!isLive || componentProgressId == null) return Promise.resolve()

      const playbackSpeed = Math.max(data.playbackSpeed, 0.25)
      const segmentWatchSec = data.wasSeeking
        ? 0
        : Math.max(0, data.endTimeSec - data.startTimeSec) / playbackSpeed
      const accumulatedWatchSec = segmentWatchSec + heartbeatRemainderSecRef.current
      const watchedDurationSec = Math.floor(accumulatedWatchSec)
      heartbeatRemainderSecRef.current = Math.max(0, accumulatedWatchSec - watchedDurationSec)

      const totalDurationSec = Math.floor(
        playerRef.current?.getDuration() || state.durationSec || videoData.duration || 0,
      )
      const request = heartbeatQueueRef.current.then(() =>
        sendVideoHeartbeat(componentProgressId, {
          enrollmentId,
          currentPositionSec: Math.floor(data.endTimeSec),
          watchedDurationSec,
          totalDurationSec,
          requiredWatchPercent: videoData.requiredWatchPercent,
        }),
      )
      heartbeatQueueRef.current = request.catch((e) => {
        console.warn('heartbeat failed', e)
      })
      return request
    },
    [
      isLive,
      componentProgressId,
      enrollmentId,
      state.durationSec,
      videoData.duration,
      videoData.requiredWatchPercent,
    ],
  )

  const handleHeartbeat = useCallback(
    (data: {
      startTimeSec: number
      endTimeSec: number
      playbackSpeed: number
      wasSeeking: boolean
    }) => {
      void queueHeartbeat(data)
    },
    [queueHeartbeat],
  )

  const clampToPausePoint = useCallback(
    (point: PauseAndGoPoint) => {
      playerRef.current?.pause()
      seekPlayer(point.time / 1000)
    },
    [seekPlayer],
  )

  const activateReachedPausePoint = useCallback(
    (time: number): boolean => {
      if (activePoint || manualSeekingRef.current) return false
      // 이어보기·지연된 timeupdate에서도 이미 지난 미확인 문제를 순서대로 복구한다.
      const triggerPoint = orderedPausePoints.find((point) => {
        const key = pausePointKey(point.time, point.quizId)
        return (
          point.time <= time * 1000 &&
          !triggeredPointsRef.current.has(key) &&
          !previousPauseKeys.has(key) &&
          !acknowledgedPausePoints.has(key)
        )
      })
      if (!triggerPoint) return false
      triggeredPointsRef.current.add(pausePointKey(triggerPoint.time, triggerPoint.quizId))
      setActivePoint(triggerPoint)
      clampToPausePoint(triggerPoint)
      return true
    },
    [activePoint, orderedPausePoints, previousPauseKeys, acknowledgedPausePoints, clampToPausePoint],
  )

  const updatePlayerProgress = state.onProgress
  const handleProgress = useCallback(
    (p: number, time: number, videoDuration: number) => {
      updatePlayerProgress(p, time, videoDuration)
      if (activateReachedPausePoint(time)) return

      // 활성 동안 cap 도달 시 자동 일시정지 (활성 유지)
      if (
        activePoint &&
        time >= activePoint.time / 1000 - PAUSE_CAP_TOLERANCE_SEC
      ) {
        clampToPausePoint(activePoint)
      }
    },
    [activePoint, activateReachedPausePoint, clampToPausePoint, updatePlayerProgress],
  )

  const handlePlayStateChange = useCallback(
    (playing: boolean) => {
      state.onPlayStateChange(playing)
      if (!isLive || componentProgressId == null) return
      const videoTimeSec = Math.floor(playerRef.current?.getCurrentTime() ?? 0)
      if (playing) playStartPositionSecRef.current = videoTimeSec
      sendVideoEvent(componentProgressId, {
        enrollmentId,
        eventType: playing ? 'play' : 'pause',
        startPositionSec: playStartPositionSecRef.current,
        endPositionSec: playing ? null : videoTimeSec,
        seekToSec: null,
        playbackSpeed: state.playbackRate,
      }).catch((e) => console.warn('video event failed', e))
    },
    [state, isLive, componentProgressId, enrollmentId],
  )

  const handleVideoComplete = useCallback(() => {
    const currentTimeSec = playerRef.current?.getCurrentTime() ?? state.currentSec
    activateReachedPausePoint(currentTimeSec)
    if (!isLive || componentProgressId == null) return
    const videoTimeSec = Math.floor(currentTimeSec)
    sendVideoEvent(componentProgressId, {
      enrollmentId,
      eventType: 'end',
      startPositionSec: playStartPositionSecRef.current,
      endPositionSec: videoTimeSec,
      seekToSec: null,
      playbackSpeed: state.playbackRate,
    }).catch((e) => console.warn('video event failed', e))
  }, [activateReachedPausePoint, isLive, componentProgressId, enrollmentId, state.currentSec, state.playbackRate])

  const handleResume = useCallback(() => {
    setActivePoint(null)
    playerRef.current?.play()
  }, [])

  const activePauseCapSec = activePoint ? activePoint.time / 1000 : null
  const playDisabled =
    activePauseCapSec != null &&
    state.currentSec >= activePauseCapSec - PAUSE_CAP_TOLERANCE_SEC

  const handlePlayerSeek = useCallback(
    (seconds: number) => {
      if (
        activePauseCapSec != null &&
        seconds >= activePauseCapSec - PAUSE_CAP_TOLERANCE_SEC
      ) {
        playerRef.current?.pause()
        seekPlayer(activePauseCapSec)
        return
      }
      seekPlayer(seconds)
    },
    [activePauseCapSec, seekPlayer],
  )

  const acknowledgeActivePoint = useCallback(() => {
    if (activePoint) {
      setAcknowledgedPausePoints((previous) => {
        const next = new Set(previous)
        next.add(pausePointKey(activePoint.time, activePoint.quizId))
        return next
      })
    }
  }, [activePoint])

  const handleGuideConfirm = useCallback(async () => {
    if (!activePoint || guideSubmittingRef.current) return
    guideSubmittingRef.current = true
    setGuideSubmitting(true)
    setError(null)
    try {
      // 퀴즈가 없는 레거시 안내는 기존처럼 화면에서만 확인한다.
      // 실제 퀴즈는 저장 성공 후에만 재개해 재진입 시에도 확인 사실을 복원한다.
      if (
        isLive && componentProgressId != null &&
        activePoint.quizId != null && /^\d+$/.test(activePoint.quizId)
      ) {
        await submitPauseQuizOfflineComplete(componentProgressId, {
          quizId: activePoint.quizId,
          pauseTimeMs: activePoint.time,
        })
      }
      acknowledgeActivePoint()
      handleResume()
    } catch (e) {
      const message = getApiErrorMessage(e, t)
      setError(message)
      toast.error(message)
    } finally {
      guideSubmittingRef.current = false
      setGuideSubmitting(false)
    }
  }, [activePoint, isLive, componentProgressId, acknowledgeActivePoint, handleResume, t])

  const clearPendingPauseAnswer = pauseState.clearPendingAnswer

  const handlePauseQuizSubmit = useCallback(
    (quizId: string) => {
      clearPendingPauseAnswer(quizId)
      acknowledgeActivePoint()
    },
    [acknowledgeActivePoint, clearPendingPauseAnswer],
  )

  const handleComplete = useCallback(async () => {
    setCompleting(true)
    setError(null)
    try {
      if (isLive && componentProgressId != null) {
        playerRef.current?.flushHeartbeat()
        await heartbeatQueueRef.current
        await completeSession(componentProgressId, {
          enrollmentId,
          componentType,
        })
      }
      onComplete()
    } catch (e) {
      console.error('completeSession failed', e)
      const msg = getApiErrorMessage(e, t)
      setError(msg)
      toast.error(msg)
      setCompleting(false)
    }
  }, [
    isLive,
    componentProgressId,
    enrollmentId,
    componentType,
    onComplete,
    t,
  ])

  const isActive = activePoint !== null
  const activePointIndex = activePoint ? pauseState.indexOf(activePoint) : -1
  const activeQuizId = activePoint
    ? String(
        (activePoint.quiz as { quizId?: string | number } | null)?.quizId ??
          activePoint.quizId ??
          '',
      )
    : ''

  const videoBlock = (
    <div
      ref={videoContainerRef}
      data-region="video-player-surface"
      className={`h-full min-h-0 w-full overflow-hidden rounded-[16px] border-2 border-black/10 bg-black [&>div]:h-full [&>div]:w-full [&>div]:aspect-auto [&>div]:rounded-none [&_video]:object-contain ${isActive ? 'pointer-events-none' : ''}`}
    >
      <QueturePlayer
        renderMetadataMath
        ref={playerRef}
        qid={extractQidFromUrl(videoData.hlsUrl) || 'video'}
        hlsUrl={hlsUrl}
        thumbnailUrl={videoData.thumbnailUrl}
        quetureApi={quetureApi}
        startTime={videoData.lastPositionSec > 0 ? videoData.lastPositionSec : 0}
        allowSkip
        externalControls
        onProgress={handleProgress}
        onHeartbeat={handleHeartbeat}
        onComplete={handleVideoComplete}
        onPlayStateChange={handlePlayStateChange}
        onPlaybackRateChange={state.onPlaybackRateChange}
        autoPlay={videoData.autoPlay && !materialBookDialogOpen}
      />
    </div>
  )

  const isGuideActive =
    isActive && activePoint != null && pauseState.effectiveMode === 'guide' && !pauseState.isCurrentSubmitted

  const activeCard =
    isActive && activePoint && !isGuideActive ? (
      <div
        data-region="pause-active-card"
        role="region"
        aria-label={t('pause.bottomCardRegionLabel') as string}
        aria-live="polite"
        className="h-full min-h-0 w-full min-w-0 overflow-y-auto overscroll-contain"
      >
        {pauseState.isCurrentSubmitted && pauseState.currentPointResponse ? (
          <PauseResultCard
            pointIndex={activePointIndex}
            response={pauseState.currentPointResponse}
            onResume={handleResume}
          />
        ) : (
          <PauseSolvePanel
            point={activePoint}
            pointIndex={activePointIndex}
            enrollmentId={enrollmentId}
            learningAttemptId={videoData.learningAttemptId}
            componentProgressId={componentProgressId}
            runtimeMode={runtimeMode}
            isDesignatedReview={isDesignatedReview}
            initialAnswers={pauseState.pendingAnswersByQuizId.get(activeQuizId)}
            onAnswerChange={(answers) =>
              pauseState.setPendingAnswer(activeQuizId, answers)
            }
            onSubmitSuccess={handlePauseQuizSubmit}
            onResume={handleResume}
          />
        )}
      </div>
    ) : null

  const playerBar = (
    <PlayerBar
      state={state}
      playerRef={playerRef}
      containerRef={containerRef}
      skipPolicy={videoData.skipPolicy}
      speedPolicy={videoData.speedPolicy}
      videoComponentEverCompleted={videoData.videoComponentEverCompleted}
      playDisabled={playDisabled}
      onSeek={handlePlayerSeek}
      maxScrubSec={isActive && activePoint ? activePoint.time / 1000 : undefined}
      pausePoints={derivedMarkers}
      appearance="v3"
    />
  )

  const completeCta = (
    <CompleteCTA
      canComplete={canComplete}
      completing={completing}
      onComplete={handleComplete}
      variant="v3"
    />
  )

  const errorCard = error ? (
    <Card size="md" role="alert" className="flex items-center gap-3">
      <div
        aria-label="error"
        className="size-8 rounded-full bg-lc-red-900 text-white flex items-center justify-center text-[18px] font-bold shrink-0"
      >
        ✕
      </div>
      <p className="text-[16px] leading-[1.5] tracking-[-0.6px] text-lc-red-dark">
        {error}
      </p>
    </Card>
  ) : null

  const primaryColumn = (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="min-h-[240px] flex-1">{videoBlock}</div>
      <div className="flex min-w-0 w-full shrink-0 items-end justify-center gap-5 pt-1">
        {playerBar}
        {!activeCard ? completeCta : null}
      </div>
    </div>
  )

  const guideCard = isGuideActive ? (
    <div
      data-region="pause-active-card"
      role="region"
      aria-label={t('pause.bottomCardRegionLabel') as string}
      aria-live="polite"
      className="w-full min-w-0 shrink-0"
    >
      <PauseGuideCard
        pointIndex={activePointIndex}
        materialNotice={materialNotice}
        submitting={guideSubmitting}
        onConfirmGuide={() => {
          void handleGuideConfirm()
        }}
      />
    </div>
  ) : null

  return (
    <>
      {materialNotice ? (
        <MaterialBookOpenDialog
          open={materialBookDialogOpen}
          notice={materialNotice}
          onConfirm={() => setMaterialBookDialogDismissed(true)}
        />
      ) : null}

      <div
        ref={containerRef}
        data-layout={isPauseQuizDisabled ? 'pause-quiz-disabled' : 'pause-quiz-enabled'}
        className="flex h-full min-h-0 w-full min-w-0 flex-col gap-3"
      >
        <div
          className={
            activeCard
              ? 'h-full min-h-[480px]'
              : 'min-h-0 flex-1'
          }
        >
          <SplitContainer
            split={splitRatio}
            onSplitChange={setSplitRatio}
            primary={primaryColumn}
            secondary={activeCard}
            breakpointPx={900}
            appearance="v3"
          />
        </div>
        {guideCard}
        {errorCard}
      </div>
    </>
  )
}
